/**
 * Live preview of a clone stamp stroke. The stroke's copies are baked (so
 * earlier moves and colour edits show) and drawn by the stock gsplat renderer
 * on the overlay layer (pick reads never see it), as a child of the edit
 * entity. It only ever holds the stroke's copies, so rebuilding it as the
 * stroke grows stays cheap; the edit entity itself is rebuilt once, when the
 * stroke is committed, and the preview stays until that rebuild draws.
 */
import * as pc from 'playcanvas';
import type { BakedSplats } from '../core/bake';

export class ClonePreview {
    private readonly app: pc.AppBase;
    private readonly layer: pc.Layer;
    private readonly entity: pc.Entity;
    private resource: pc.GSplatResource | null = null;

    constructor(app: pc.AppBase, parent: pc.Entity, layer: pc.Layer) {
        this.app = app;
        this.layer = layer;
        this.entity = new pc.Entity('splat-edit-clone-preview');
        parent.addChild(this.entity);
    }

    update(baked: BakedSplats): void {
        this._release();
        if (!baked.numSplats) return;
        const properties: { type: string; name: string; storage: Float32Array; byteSize: number }[] = [];
        for (const [name, storage] of baked.columns) properties.push({ type: 'float', name, storage, byteSize: 4 });
        const data = new pc.GSplatData([{ name: 'vertex', count: baked.numSplats, properties } as never]);
        this.resource = new pc.GSplatResource(this.app.graphicsDevice, data);
        this.entity.addComponent('gsplat', {
            resource: this.resource,
            unified: false,
            layers: [this.layer.id],
        } as never);
    }

    destroy(): void {
        this._release();
        this.entity.destroy();
    }

    private _release(): void {
        if (this.entity.gsplat) this.entity.removeComponent('gsplat');
        this.resource?.destroy();
        this.resource = null;
    }
}
